import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ArrowRight, ArrowLeft, Sparkles, Wrench } from "lucide-react";
import { api } from "@/lib/api";

const STEPS = [
  {
    id: "scope",
    q: "What kind of project is this?",
    sub: "Pick the one that fits best.",
    opts: [
      { v: "home", label: "Home repair / improvement", help: "Plumbing, electrical, painting, roofing…" },
      { v: "outdoor", label: "Outdoor / landscaping", help: "Lawn, trees, fence, pool, deck…" },
      { v: "cleaning", label: "Cleaning / maintenance", help: "House, carpets, gutters, junk removal…" },
      { v: "tech", label: "Tech / electronics", help: "Computer, TV, network, smart home…" },
      { v: "events", label: "Event / creative", help: "Photo, video, DJ, catering, planning…" },
      { v: "personal", label: "Personal / wellness / pets", help: "Training, massage, tutoring, pets…" },
      { v: "auto", label: "Auto", help: "Repair, detailing, mobile mechanic…" },
      { v: "business", label: "Business services", help: "Web, marketing, accounting…" },
      { v: "moving", label: "Moving / transport", help: "Local or long-distance moving…" },
    ],
  },
  {
    id: "urgency",
    q: "How soon do you need it done?",
    sub: "Pros use this to prioritize their pipeline.",
    opts: [
      { v: "asap", label: "ASAP — within 48 hours" },
      { v: "weeks", label: "Within a couple of weeks" },
      { v: "flexible", label: "I'm flexible / just planning" },
    ],
  },
  {
    id: "budget",
    q: "What's your budget?",
    sub: "Helps us match pros who handle projects your size.",
    opts: [
      { v: "small", label: "Under $500" },
      { v: "med", label: "$500 – $2,500" },
      { v: "large", label: "$2,500 – $10,000" },
      { v: "xl", label: "$10,000+" },
    ],
  },
];

// Map scope answers → preferred category slugs (highest priority first).
// We pick from this list and intersect with what actually exists in the DB.
const SCOPE_TO_SLUGS = {
  home: ["plumbing","electrical","hvac","handyman","painting","roofing","carpentry","drywall","appliance-repair","windows","doors","garage-doors","locksmith","pest-control","flooring","tile","concrete"],
  outdoor: ["landscaping","lawn-mowing","tree-service","pool-service","fence","deck-patio"],
  cleaning: ["cleaning","carpet-cleaning","pressure-washing","gutter-cleaning","junk-removal","chimney"],
  tech: ["computer-repair","network-support","tv-mounting","home-theater","smart-home","security-systems","solar"],
  events: ["photography","videography","dj","catering","event-planning"],
  personal: ["personal-training","massage","tutoring","dog-training","pet-care"],
  auto: ["auto-repair","auto-detailing"],
  business: ["web-design","marketing","accounting"],
  moving: ["moving","long-distance-moving"],
  remodel: ["kitchen-remodel","bathroom-remodel","interior-design"],
};

// Budget tier preferences (slugs that tend to align with that price band)
const BUDGET_PREF = {
  small: ["handyman","tv-mounting","cleaning","carpet-cleaning","lawn-mowing","junk-removal","auto-detailing","pet-care","tutoring"],
  med:   ["plumbing","electrical","painting","carpentry","appliance-repair","drywall","tree-service","photography","computer-repair"],
  large: ["roofing","flooring","windows","fence","deck-patio","hvac","interior-design","catering","videography","smart-home","security-systems"],
  xl:    ["kitchen-remodel","bathroom-remodel","solar","concrete"],
};

function pickMatches(answers, categories) {
  const slugs = SCOPE_TO_SLUGS[answers.scope] || [];
  const budgetSlugs = BUDGET_PREF[answers.budget] || [];
  // Score: 2pts if in scope list, +1 if also in budget preference
  const scored = [];
  for (const c of categories) {
    let s = 0;
    if (slugs.includes(c.slug)) s += 2;
    if (budgetSlugs.includes(c.slug)) s += 1;
    if (s > 0) scored.push({ c, s });
  }
  scored.sort((a, b) => b.s - a.s);
  // Promote remodel suggestions if budget is xl
  if (answers.budget === "xl" && answers.scope === "home") {
    const remodels = categories.filter((c) => SCOPE_TO_SLUGS.remodel.includes(c.slug));
    remodels.forEach((c) => {
      if (!scored.find((x) => x.c.category_id === c.category_id)) scored.unshift({ c, s: 3 });
    });
  }
  return scored.slice(0, 4).map((x) => x.c);
}

export default function FindCategoryQuiz({ onClose }) {
  const [categories, setCategories] = useState([]);
  const [popular, setPopular] = useState({});
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [sessionId, setSessionId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/categories/popular").then((r) => setPopular(r.data.by_slug || {})).catch(() => {});
  }, []);

  const select = (v) => {
    const k = STEPS[step].id;
    const next = { ...answers, [k]: v };
    setAnswers(next);
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      // Log the quiz event with matched slugs
      const matches = pickMatches(next, categories);
      api.post("/quiz/log", {
        scope: next.scope, urgency: next.urgency, budget: next.budget,
        matched_slugs: matches.map((c) => c.slug),
      }).then((r) => setSessionId(r.data.session_id)).catch(() => {});
      setStep(STEPS.length);
    }
  };

  const back = () => setStep(Math.max(0, step - 1));

  const goPost = (catId) => {
    onClose?.();
    navigate("/customer/post", { state: { category_id: catId, quiz_session_id: sessionId } });
  };

  const matches = step === STEPS.length ? pickMatches(answers, categories) : [];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="quiz-modal">
      <div className="absolute inset-0 bg-ink/60" onClick={onClose} />
      <div className="relative brut-card bg-white w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="border-b-2 border-ink p-5 bg-ink text-acid flex items-center justify-between">
          <div>
            <div className="overline text-acid">// Find your pro</div>
            <div className="font-display font-bold uppercase text-xl mt-1 leading-none">
              {step < STEPS.length ? `Step ${step + 1} of ${STEPS.length}` : "Your matches"}
            </div>
          </div>
          <button onClick={onClose} className="bg-acid text-ink border-2 border-acid w-9 h-9 flex items-center justify-center" data-testid="quiz-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Progress bar */}
        {step < STEPS.length && (
          <div className="border-b-2 border-ink flex">
            {STEPS.map((s, i) => (
              <div key={`progress-${s.id}`} className={`flex-1 h-2 border-r-2 last:border-r-0 border-ink ${i <= step ? "bg-acid" : "bg-bone"}`} />
            ))}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8">
          {step < STEPS.length ? (
            <>
              <h2 className="font-display font-black text-3xl uppercase tracking-tight leading-tight" data-testid={`quiz-q-${STEPS[step].id}`}>
                {STEPS[step].q}
              </h2>
              <p className="text-muted-foreground mt-2">{STEPS[step].sub}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6">
                {STEPS[step].opts.map((o) => (
                  <button
                    key={o.v}
                    onClick={() => select(o.v)}
                    className="text-left border-2 border-ink p-4 bg-white hover:bg-acid transition-colors font-mono"
                    data-testid={`quiz-opt-${STEPS[step].id}-${o.v}`}
                  >
                    <div className="font-display font-bold uppercase text-sm">{o.label}</div>
                    {o.help && <div className="text-xs mt-1 opacity-70">{o.help}</div>}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-klein" />
                <span className="overline">// Top recommendations</span>
              </div>
              <h2 className="font-display font-black text-3xl uppercase tracking-tight mt-3 leading-tight">
                We found {matches.length} pro {matches.length === 1 ? "category" : "categories"} for you.
              </h2>
              <p className="text-muted-foreground mt-2 text-sm">Click one to start a project — takes 60 seconds.</p>
              {matches.length === 0 ? (
                <div className="mt-6 border-2 border-ink p-6 text-center bg-bone">
                  <p className="font-mono uppercase text-xs tracking-widest">No exact match — browse all categories instead.</p>
                  <button onClick={() => { onClose?.(); navigate("/categories"); }} className="brut-btn-primary mt-4" data-testid="quiz-browse-all">
                    Browse all categories
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-2 border-ink mt-6">
                  {matches.map((c) => {
                    const pop = popular[c.slug];
                    return (
                      <button
                        key={c.category_id}
                        onClick={() => goPost(c.category_id)}
                        className="text-left border-r-2 border-b-2 border-ink p-5 bg-white hover:bg-acid transition-colors group last:border-b-0 md:[&:nth-last-child(-n+2)]:border-b-0"
                        data-testid={`quiz-match-${c.slug}`}
                      >
                        <Wrench className="w-5 h-5 mb-2" />
                        <div className="font-display font-bold uppercase text-lg leading-tight">{c.name}</div>
                        <div className="font-mono text-xs mt-2 flex items-center justify-between">
                          <span>${c.lead_price.toFixed(0)} / lead</span>
                          <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100" />
                        </div>
                        {pop && pop.share >= 1 && (
                          <div
                            className="font-mono text-[10px] uppercase tracking-widest mt-3 inline-block border-2 border-ink bg-acid px-2 py-0.5"
                            data-testid={`quiz-social-${c.slug}`}
                          >
                            {pop.share}% chose this month
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t-2 border-ink p-4 flex justify-between items-center bg-bone">
          <button onClick={back} disabled={step === 0} className="brut-btn disabled:opacity-30" data-testid="quiz-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </button>
          {step >= STEPS.length ? (
            <button onClick={() => { setStep(0); setAnswers({}); }} className="brut-btn" data-testid="quiz-restart">
              Start over
            </button>
          ) : (
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Pick an option to continue →</span>
          )}
        </div>
      </div>
    </div>
  );
}
